import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import { getRankingUpdates } from '../../services/ranking.service';
import { Box, IconButton, Tooltip } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import UndoIcon from '@mui/icons-material/Undo';
import { RANKING_ESTADO, RANKING_ESTADO_LABEL } from '../../constants/ranking-estado';

const RankingUpdatesTable = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const result = await getRankingUpdates();
        setData(result || []);
      } catch (error) {
        setIsError(true);
        console.error('Error fetching ranking updates:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  const hayBorrador = useMemo(
    () => data.some((r) => Number(r.estado) === RANKING_ESTADO.CREADO),
    [data]
  );

  const idPrimerProcesadoRollbackable = useMemo(() => {
    const procesados = data.filter((r) => Number(r.estado) === RANKING_ESTADO.GRAU_CONFIRMADO);
    if (!procesados.length) return null;
    return procesados.reduce(
      (maxId, r) => (r.id > maxId ? r.id : maxId),
      procesados[0].id
    );
  }, [data]);

  const columns = useMemo(
    () => [
      {
        accessorKey: 'id',
        header: 'ID Ranking',
        size: 100,
        muiTableBodyCellProps: { align: 'center' },
        muiTableHeadCellProps: { align: 'center' },
      },
      {
        accessorKey: 'fecha',
        header: 'Fecha de Actualización',
        size: 180,
        muiTableBodyCellProps: { align: 'center' },
        muiTableHeadCellProps: { align: 'center' },
        Cell: ({ cell }) => {
          const val = cell.getValue();
          if (!val) return '-';
          if (typeof val === 'string' && val.includes('-')) {
            const parts = val.split('T')[0].split('-');
            if (parts.length === 3) {
              const [year, month, day] = parts;
              return `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year}`;
            }
          }
          const d = new Date(val);
          if (isNaN(d.getTime())) return val;
          const dd = String(d.getUTCDate()).padStart(2, '0');
          const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
          const yyyy = d.getUTCFullYear();
          return `${dd}/${mm}/${yyyy}`;
        },
      },
      {
        accessorKey: 'estado',
        header: 'Estado',
        size: 120,
        muiTableBodyCellProps: { align: 'center' },
        muiTableHeadCellProps: { align: 'center' },
        Cell: ({ cell }) => RANKING_ESTADO_LABEL[Number(cell.getValue())] ?? '—',
      },
      {
        accessorKey: 'cantTorneos',
        header: 'Torneos Computados',
        size: 150,
        muiTableBodyCellProps: { align: 'center' },
        muiTableHeadCellProps: { align: 'center' },
      },
      {
        accessorKey: 'cantJugadores',
        header: 'Jugadores Afectados',
        size: 160,
        muiTableBodyCellProps: { align: 'center' },
        muiTableHeadCellProps: { align: 'center' },
      },
      {
        accessorKey: 'permiteRollback',
        header: 'Permite Rollback',
        size: 150,
        muiTableBodyCellProps: { align: 'center' },
        muiTableHeadCellProps: { align: 'center' },
        Cell: ({ cell }) => (cell.getValue() ? 'Sí' : 'No'),
      },
    ],
    []
  );

  const table = useMaterialReactTable({
    columns,
    data,
    enableColumnActions: false,
    enableGlobalFilter: true,
    enableHiding: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableRowActions: true,
    renderRowActions: ({ row }) => {
      const estado = Number(row.original.estado);
      const esConfirmado = estado === RANKING_ESTADO.GRAU_CONFIRMADO;
      const handleVer = () => {
        if (esConfirmado) {
          navigate(`/ranking-management/view/${row.original.id}`);
        } else {
          navigate(`/ranking-management/edit/${row.original.id}`);
        }
      };
      return (
      <Box sx={{ display: 'flex' }}>
        <Tooltip title={esConfirmado ? 'Ver detalle' : 'Ver / Editar'}>
          <IconButton style={{ color: '#1abbed' }} onClick={handleVer}>
            <SearchIcon />
          </IconButton>
        </Tooltip>
        {[
            RANKING_ESTADO.CREADO,
            RANKING_ESTADO.PARTIDAS_VALIDADAS,
            RANKING_ESTADO.GRAU_CALCULADO,
          ].includes(Number(row.original.estado)) && (
          <Tooltip title="Editar">
            <IconButton
              style={{ color: '#f59e0b' }}
              onClick={() => navigate(`/ranking-management/edit/${row.original.id}`)}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
        )}
        {[
            RANKING_ESTADO.CREADO,
            RANKING_ESTADO.PARTIDAS_VALIDADAS,
            RANKING_ESTADO.GRAU_CALCULADO,
          ].includes(Number(row.original.estado)) && (
          <Tooltip title="Eliminar">
            <IconButton
              style={{ color: '#ef4444' }}
              onClick={() => { }}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        )}
        {Number(row.original.estado) === RANKING_ESTADO.GRAU_CONFIRMADO &&
          !hayBorrador &&
          row.original.id === idPrimerProcesadoRollbackable && (
            <Tooltip title="Deshacer">
              <IconButton
                style={{ color: '#a855f7' }}
                onClick={() => { }}
              >
                <UndoIcon />
              </IconButton>
            </Tooltip>
          )}
      </Box>
      );
    },
    positionActionsColumn: 'last',
    displayColumnDefOptions: {
      'mrt-row-actions': {
        header: 'Acciones',
        muiTableHeadCellProps: { align: 'center' },
        muiTableBodyCellProps: { align: 'center' },
      },
    },
    getRowId: (row) => row.id,
    initialState: { density: 'compact' },
    muiToolbarAlertBannerProps: isError
      ? {
        color: 'error',
        children: 'Error cargando datos de actualizaciones de ranking',
      }
      : undefined,
    state: {
      isLoading,
      showAlertBanner: isError,
    },
    muiTableHeadCellProps: {
      sx: {
        background: '#283f50',
        color: '#cacbd1',
      },
    },
    muiTableBodyCellProps: {
      sx: {
        background: '#20232f',
        color: '#ffffffb3',
      },
    },
  });

  return <MaterialReactTable table={table} />;
};

export default RankingUpdatesTable;
